import router from '@/router';
import usuarioModule from "@/store/usuario/UsuarioModule";
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

export default abstract class Api {
  private api: AxiosInstance;

  public constructor(config: AxiosRequestConfig) {
    this.api = axios.create(config);

    // // this middleware is been called right before the http request is made.
    this.api.interceptors.request.use((param: AxiosRequestConfig) => {
      if (usuarioModule?.token && param.url != "/oauth/token") {
        return {
          ...param,
          auth: undefined,
          headers: {
            common: {
              "access-control-allow-origin": "*",
              "Cache-Control": "no-cache, no-store, must-revalidate",
              "Content-Type": "application/json; charset=utf-8",
              "Accept": "application/json, text/plain, application/octet-stream,image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8",
            },
            Authorization: usuarioModule?.authData?.access_token
              ? `Bearer ${usuarioModule.authData?.access_token}`
              : undefined,
          },
        };
      } else {
        return {
          ...param,
        };
      }
    });

    // // this middleware is been called right before the response is get it by the method that triggers the request
    this.api.interceptors.response.use((param: AxiosResponse) => {
      return {
        ...param
      }
    }, 
    (error: AxiosError) => {
      if (error.response?.status !== 401) {
        return Promise.reject(error);
      }
      
      if(error.response?.config.url == '/usuarios/resetSenhaAdministrativo') {
        return Promise.reject(error);
      }

      if(error.response?.config.url == '/oauth/token') {
        usuarioModule.logout()
        .finally(() => {
          router.push("/")
          .finally(() => {
            return Promise.reject(error);
          });
        });
      }

      return usuarioModule.refreshToken()
        .then(() => {
          // New request with new token
          const config = error.config;
          config.headers['Authorization'] = `Bearer ${usuarioModule.authData?.access_token}`;

          return new Promise((resolve, reject) => {
            axios.request(config).then(response => {
              resolve(response);
            }).catch((error) => {
              reject(error);
            })
          });
        })
        .catch((error) => {
          usuarioModule.logout();
          router.push("/");
          Promise.reject(error);
        });
    });
  }

  public getUri(config?: AxiosRequestConfig): string {
    return this.api.getUri(config);
  }

  public request<T, R = AxiosResponse<T>>(
    config: AxiosRequestConfig
  ): Promise<R> {
    return this.api.request(config);
  }

  public get<T, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.get(url, {...config, data: {}});
  }

  public delete<T, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.delete(url, config);
  }

  public head<T, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.head(url, config);
  }

  public post<T, R = AxiosResponse<T>>(
    url: string,
    data?: string | Object,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.post(url, data, config);
  }

  public put<T, R = AxiosResponse<T>>(
    url: string,
    data?: string | Object,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.put(url, data, config);
  }

  public patch<T, R = AxiosResponse<T>>(
    url: string,
    data?: string | Object,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.patch(url, data, config);
  }
}
